import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import { toast } from "react-toastify";

import type { UseCrmSetupConfigurationResult } from "./useCrmSetupConfiguration";
import type { CrmSetupConfigurationOption } from "../types";
import {
  fetchWabaPhoneNumbers,
  pickPhoneContacts,
  type WhatsAppBusinessPhoneContact,
} from "../services/whatsappBusinessSetupService";
import {
  MANUAL_WABA_LOOKUP_DEBOUNCE_MS,
  MANUAL_WABA_LOOKUP_MIN_LENGTH,
} from "../constants/crmSetupConfigurationOptions";
import { readFluxmedCardHighlightSessionFlag } from "../utils/crmSetupConfigurationOptions";

export interface UseCrmSetupConfigurationOptionsPanelResult {
  selectedOption: UseCrmSetupConfigurationResult["configurationStatus"] extends infer T ? T extends { selectedOption?: infer S } ? S : never : never | null;
  metaConnection: UseCrmSetupConfigurationResult["configurationStatus"] extends infer T ? T extends { metaConnection?: infer M } ? M : never : never | null;
  isConfirmDialogOpen: boolean;
  isFluxmedConfirmOpen: boolean;
  metaConnectionMode: "embedded" | "manual";
  manualWabaId: string;
  selectedManualPhoneContactId: string | null;
  manualPhoneContacts: WhatsAppBusinessPhoneContact[];
  isLoadingManualPhoneContacts: boolean;
  manualPhoneLookupError: string | null;
  manualValidationError: string | null;
  hasManualWabaLookupSucceeded: boolean;
  isManualWabaInputVisible: boolean;
  partnerIdCopied: boolean;
  isUsingDefaultOption: boolean;
  isUsingMetaOption: boolean;
  shouldDimMetaCard: boolean;
  shouldDimFluxmedCard: boolean;
  isMetaFullyConnected: boolean;
  isMetaCardHighlighted: boolean;
  showMetaDisconnectButton: boolean;
  showFluxmedCardHighlight: boolean;
  handleConnectMetaClick: () => void;
  handleConfirmConnectMeta: () => Promise<void>;
  handleCancelConnectMeta: () => void;
  handleMetaConnectionModeChange: (
    event: MouseEvent<HTMLElement>,
    nextMode: "embedded" | "manual" | null,
  ) => void;
  handleCopyPartnerId: () => void;
  handleManualWabaIdChange: (value: string) => void;
  handleShowManualWabaInput: () => void;
  handleSelectManualPhoneContact: (id: string) => void;
  handleManualLinking: () => Promise<void>;
  handleSelectFluxmedClick: () => void;
  handleConfirmFluxmed: () => Promise<void>;
  handleCancelFluxmed: () => void;
}

export function useCrmSetupConfigurationOptionsPanel(
  params: UseCrmSetupConfigurationResult,
  options: {
    isFluxZapExtensionActive?: boolean;
    hasConfiguredAutomations?: boolean;
    removeAllAutomationConfigs?: () => Promise<void>;
    /** Canal já persistido no backend (antes da troca no modal). */
    persistedWhatsappChannelOption?: CrmSetupConfigurationOption | null;
    onAfterChannelChange?: () => void | Promise<void>;
  } = {},
): UseCrmSetupConfigurationOptionsPanelResult {
  const isFluxZapExtensionActive = options.isFluxZapExtensionActive ?? false;
  const hasConfiguredAutomations = options.hasConfiguredAutomations ?? false;
  const removeAllAutomationConfigs = options.removeAllAutomationConfigs;
  const persistedWhatsappChannelOption =
    options.persistedWhatsappChannelOption ?? null;
  const onAfterChannelChange = options.onAfterChannelChange;
  const {
    configurationStatus,
    isMetaConnected,
    setConfigurationOption,
    connectMetaAccount,
    setManualMetaConnection,
    disconnectMetaAccount,
  } = params;

  const selectedOption = configurationStatus?.selectedOption ?? null;
  const metaConnection = configurationStatus?.metaConnection ?? null;

  const [fluxmedCardHighlightFromClick, setFluxmedCardHighlightFromClick] = useState(readFluxmedCardHighlightSessionFlag);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isFluxmedConfirmOpen, setIsFluxmedConfirmOpen] = useState(false);
  const [metaConnectionMode, setMetaConnectionMode] = useState<"embedded" | "manual">("embedded");
  const [manualWabaId, setManualWabaId] = useState("");
  const [manualPrimaryPhoneValueHint, setManualPrimaryPhoneValueHint] = useState<string | null>(null);
  const [selectedManualPhoneContactId, setSelectedManualPhoneContactId] = useState<string | null>(null);
  const [manualPhoneContacts, setManualPhoneContacts] = useState<WhatsAppBusinessPhoneContact[]>([]);
  const [isLoadingManualPhoneContacts, setIsLoadingManualPhoneContacts] = useState(false);
  const [manualPhoneLookupError, setManualPhoneLookupError] = useState<string | null>(null);
  const [manualValidationError, setManualValidationError] = useState<string | null>(null);
  const [hasManualWabaLookupSucceeded, setHasManualWabaLookupSucceeded] = useState(false);
  const [isManualWabaInputVisible, setIsManualWabaInputVisible] = useState(false);
  const [partnerIdCopied, setPartnerIdCopied] = useState(false);
  const partnerIdCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tryApplyManualContactsFromEndpointForWabaId = useCallback(
    (normalizedWabaId: string): boolean => {
      if (!metaConnection) return false;
      const wabaId = metaConnection.wabaId?.trim() ?? "";
      if (!wabaId || wabaId !== normalizedWabaId.trim()) return false;
      const phoneId = metaConnection.primaryPhoneNumberId?.trim() ?? "";
      const phoneVal = metaConnection.primaryPhoneValue?.trim() ?? "";
      if (!phoneId || !phoneVal) return false;
      setManualPhoneContacts([{ id: phoneId, displayPhoneNumber: phoneVal }]);
      setSelectedManualPhoneContactId(phoneId);
      setManualPrimaryPhoneValueHint(phoneVal);
      setHasManualWabaLookupSucceeded(true);
      setManualPhoneLookupError(null);
      setIsLoadingManualPhoneContacts(false);
      return true;
    },
    [metaConnection],
  );

  const handleCopyPartnerId = useCallback(() => {
    void navigator.clipboard.writeText("933366781827729").then(() => {
      setPartnerIdCopied(true);
      if (partnerIdCopyTimeoutRef.current) clearTimeout(partnerIdCopyTimeoutRef.current);
      partnerIdCopyTimeoutRef.current = setTimeout(() => setPartnerIdCopied(false), 2000);
    });
  }, []);

  const handleConnectMetaClick = useCallback(() => {
    if (!isFluxZapExtensionActive) return;
    setIsConfirmDialogOpen(true);
  }, [isFluxZapExtensionActive]);

  const handleConfirmConnectMeta = useCallback(async () => {
    if (!isFluxZapExtensionActive) return;
    setIsConfirmDialogOpen(false);
    await connectMetaAccount();
  }, [connectMetaAccount, isFluxZapExtensionActive]);

  const handleCancelConnectMeta = useCallback(() => {
    setIsConfirmDialogOpen(false);
  }, []);

  const handleMetaConnectionModeChange = useCallback(
    (_event: MouseEvent<HTMLElement>, nextMode: "embedded" | "manual" | null) => {
      if (nextMode == null) return;
      setMetaConnectionMode(nextMode);
      setIsManualWabaInputVisible(false);
      setManualValidationError(null);
      setManualPhoneLookupError(null);
    },
    [],
  );

  const handleManualWabaIdBlur = useCallback(async () => {
    const normalizedWabaId = manualWabaId.trim();
    if (!normalizedWabaId) {
      setManualPhoneContacts([]);
      setManualPhoneLookupError(null);
      setHasManualWabaLookupSucceeded(false);
      return;
    }

    if (tryApplyManualContactsFromEndpointForWabaId(normalizedWabaId)) {
      return;
    }

    setIsLoadingManualPhoneContacts(true);
    setManualPhoneLookupError(null);

    try {
      const phoneNumbersResponse = await fetchWabaPhoneNumbers(normalizedWabaId);
      const phoneContacts = pickPhoneContacts(phoneNumbersResponse);
      setManualPhoneContacts(phoneContacts);
      setHasManualWabaLookupSucceeded(true);
      setSelectedManualPhoneContactId((currentValue) => {
        if (currentValue && phoneContacts.some((phoneContact) => phoneContact.id === currentValue)) {
          return currentValue;
        }
        const currentPrimaryPhoneValue = manualPrimaryPhoneValueHint?.trim();
        if (currentPrimaryPhoneValue) {
          const matchingByValue = phoneContacts.find(
            (phoneContact) => phoneContact.displayPhoneNumber === currentPrimaryPhoneValue,
          );
          if (matchingByValue) return matchingByValue.id;
        }
        return phoneContacts[0]?.id ?? null;
      });
    } catch {
      setManualPhoneContacts([]);
      setSelectedManualPhoneContactId(null);
      setHasManualWabaLookupSucceeded(false);
      setManualPhoneLookupError(
        "Não foi possível listar os números deste WABA ID. Verifique o valor e tente novamente ou siga os passos para vincular manualmente.",
      );
    } finally {
      setIsLoadingManualPhoneContacts(false);
    }
  }, [manualPrimaryPhoneValueHint, manualWabaId, tryApplyManualContactsFromEndpointForWabaId]);

  const handleManualLinking = useCallback(async () => {
    if (!isFluxZapExtensionActive) return;
    const normalizedWabaId = manualWabaId.trim();
    if (!normalizedWabaId) {
      setManualValidationError("Informe o WABA ID para salvar a vinculação manual.");
      return;
    }
    if (!selectedManualPhoneContactId) {
      setManualValidationError("Selecione o número principal para salvar no Endpoint.");
      return;
    }

    const selectedPhoneContact = manualPhoneContacts.find(
      (phoneContact) => phoneContact.id === selectedManualPhoneContactId,
    );
    if (!selectedPhoneContact) {
      setManualValidationError("Seleção de número inválida. Recarregue os números e tente novamente.");
      return;
    }

    setManualValidationError(null);
    await setManualMetaConnection({
      wabaId: normalizedWabaId,
      primaryPhoneNumberId: selectedPhoneContact.id,
      primaryPhoneValue: selectedPhoneContact.displayPhoneNumber,
    });
  }, [isFluxZapExtensionActive, manualPhoneContacts, manualWabaId, selectedManualPhoneContactId, setManualMetaConnection]);

  const handleSelectFluxmedClick = useCallback(() => {
    setIsFluxmedConfirmOpen(true);
  }, []);

  const handleConfirmFluxmed = useCallback(async () => {
    setIsFluxmedConfirmOpen(false);

    const isChannelChangeToFluxmed =
      persistedWhatsappChannelOption !== "fluxmedDefault";

    if (
      isChannelChangeToFluxmed &&
      hasConfiguredAutomations &&
      removeAllAutomationConfigs
    ) {
      await removeAllAutomationConfigs();
      toast.info(
        "Automações personalizadas removidas. Configure-as novamente quando quiser.",
      );
    }

    if (isMetaConnected) {
      await disconnectMetaAccount();
    }
    await setConfigurationOption("fluxmedDefault");
    await onAfterChannelChange?.();
  }, [
    disconnectMetaAccount,
    hasConfiguredAutomations,
    isMetaConnected,
    onAfterChannelChange,
    persistedWhatsappChannelOption,
    removeAllAutomationConfigs,
    setConfigurationOption,
  ]);

  const handleCancelFluxmed = useCallback(() => {
    setIsFluxmedConfirmOpen(false);
  }, []);

  const isUsingDefaultOption = selectedOption === "fluxmedDefault";
  const isUsingMetaOption = selectedOption === "metaEmbeddedSignup";

  const shouldDimMetaCard = !isFluxZapExtensionActive;
  const shouldDimFluxmedCard = false;
  const isMetaFullyConnected = isMetaConnected;
  const isMetaCardHighlighted = isUsingMetaOption || isMetaConnected;
  const showMetaDisconnectButton = isUsingMetaOption || isMetaConnected;
  const showFluxmedCardHighlight = isUsingDefaultOption && fluxmedCardHighlightFromClick;

  useEffect(() => {
    if (!metaConnection) {
      setManualWabaId("");
      setManualPrimaryPhoneValueHint(null);
      setSelectedManualPhoneContactId(null);
      setManualPhoneContacts([]);
      setHasManualWabaLookupSucceeded(false);
      return;
    }
    setManualWabaId(metaConnection.wabaId ?? "");
    setManualPrimaryPhoneValueHint(metaConnection.primaryPhoneValue ?? null);
    setSelectedManualPhoneContactId(metaConnection.primaryPhoneNumberId ?? null);

    const wabaId = metaConnection.wabaId?.trim() ?? "";
    const phoneId = metaConnection.primaryPhoneNumberId?.trim() ?? "";
    const phoneVal = metaConnection.primaryPhoneValue?.trim() ?? "";
    if (wabaId && phoneId && phoneVal) {
      setManualPhoneContacts([{ id: phoneId, displayPhoneNumber: phoneVal }]);
      setHasManualWabaLookupSucceeded(true);
      setManualPhoneLookupError(null);
    } else {
      setManualPhoneContacts([]);
      setHasManualWabaLookupSucceeded(false);
    }
  }, [metaConnection]);

  useEffect(() => {
    if (metaConnectionMode !== "manual") return;
    if (metaConnection?.wabaId || hasManualWabaLookupSucceeded || manualPhoneContacts.length > 0) {
      setIsManualWabaInputVisible(true);
    }
  }, [
    hasManualWabaLookupSucceeded,
    manualPhoneContacts.length,
    metaConnection?.wabaId,
    metaConnectionMode,
  ]);

  useEffect(() => {
    if (metaConnectionMode !== "manual" || !isManualWabaInputVisible) return;
    const normalizedWabaId = manualWabaId.trim();
    if (normalizedWabaId.length < MANUAL_WABA_LOOKUP_MIN_LENGTH) return;

    if (tryApplyManualContactsFromEndpointForWabaId(normalizedWabaId)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      void handleManualWabaIdBlur();
    }, MANUAL_WABA_LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [
    handleManualWabaIdBlur,
    isManualWabaInputVisible,
    manualWabaId,
    metaConnectionMode,
    tryApplyManualContactsFromEndpointForWabaId,
  ]);

  const handleManualWabaIdChange = useCallback(
    (value: string) => {
      setManualWabaId(value);
      setSelectedManualPhoneContactId(null);
      setManualPhoneLookupError(null);
      setManualPhoneContacts([]);
      setHasManualWabaLookupSucceeded(false);
      if (manualValidationError) setManualValidationError(null);
    },
    [manualValidationError],
  );

  const handleShowManualWabaInput = useCallback(() => {
    setIsManualWabaInputVisible(true);
  }, []);

  const handleSelectManualPhoneContact = useCallback(
    (id: string) => {
      setSelectedManualPhoneContactId(id);
      if (manualValidationError) setManualValidationError(null);
    },
    [manualValidationError],
  );

  return {
    selectedOption,
    metaConnection,
    isConfirmDialogOpen,
    isFluxmedConfirmOpen,
    metaConnectionMode,
    manualWabaId,
    selectedManualPhoneContactId,
    manualPhoneContacts,
    isLoadingManualPhoneContacts,
    manualPhoneLookupError,
    manualValidationError,
    hasManualWabaLookupSucceeded,
    isManualWabaInputVisible,
    partnerIdCopied,
    isUsingDefaultOption,
    isUsingMetaOption,
    shouldDimMetaCard,
    shouldDimFluxmedCard,
    isMetaFullyConnected,
    isMetaCardHighlighted,
    showMetaDisconnectButton,
    showFluxmedCardHighlight,
    handleConnectMetaClick,
    handleConfirmConnectMeta,
    handleCancelConnectMeta,
    handleMetaConnectionModeChange,
    handleCopyPartnerId,
    handleManualWabaIdChange,
    handleShowManualWabaInput,
    handleSelectManualPhoneContact,
    handleManualLinking,
    handleSelectFluxmedClick,
    handleConfirmFluxmed,
    handleCancelFluxmed,
  };
}
